import { i18n } from '@/i18n/i18n'
import {
  type PageChoice,
  type PageChoiceData,
  type PageContent,
  type PageContentLocation,
  type PageContentWithElements,
  type PageElement,
  type PageMainImageContent,
  type PageSubMainImageContent,
  sortCarouselEventIds,
  sortCarouselGuestIds,
} from '@/pages/menuEntries/pageSetup/pageContent.type'
import { PageContentService } from '@/pages/menuEntries/pageSetup/pageContentService'
import { PAGE_MODE, type PageMode } from '@/shared/constants'
import { ErrorService } from '@/shared/service/errorService'
import { SnackbarService } from '@/shared/snackbar/snackbar.service'

import { createStore, type GettersObj } from '../../../utils/createStore'

// long enough to read the message, which asks to redo the last change
const MODIFIED_ELSEWHERE_MESSAGE_MS = 10000

/** The server refuses a list of blocks that no longer matches the page (added or deleted elsewhere). */
function isModifiedElsewhere(e: unknown): boolean {
  return (
    (e as { response?: { status?: number } } | null)?.response?.status === 409
  )
}

// blocks keep the server's `_id` (see editContent)
function serverId(content: object): string | undefined {
  return (content as { _id?: string })._id
}

export type GettersPageSetup = GettersObj<typeof mapGettersPageSetup>

export class PageSetupState {
  mode: PageMode = PAGE_MODE.VIEW
  currentPage: PageContentLocation = 'HOME_PAGE'
  pageContents: Omit<PageContent, 'page'>[] = []
  pageChoices: PageChoice[] = []
  eventChoices: PageChoiceData[] = []
  currentElement:
    | {
        parentContent: PageContentWithElements
        element: PageElement | undefined
        wantedIndex: number | undefined
      }
    | undefined = undefined
  selectedContent: Omit<PageContent, 'page'> | undefined = undefined
  showEditElementModal = false
  showDeleteElementModal = false
  showVisibilityElementModal = false
  showEditContentModal = false
  showDeleteContentModal = false
  showVisibilityContentModal = false
  loading = 0
  mainImageTemp: string | null = null
  subImageTemp: string | null = null
  ratioTemp: number | null = null
  subImageRatioTemp: number | null = null
}

export const {
  pageSetup,
  commit: commitPageSetup,
  dispatch: dispatchPageSetup,
  mapGetters: mapGettersPageSetup,
  mapState: mapStatePageSetup,
  useGetter: useGetterPageSetup,
  useState: useStatePageSetup,
} = createStore({
  moduleName: 'pageSetup',
  initState: new PageSetupState(),
  mutations: {
    LOAD(state: PageSetupState) {
      state.loading++
    },
    UNLOAD(state: PageSetupState) {
      state.loading--
    },
    SHOW_EDIT_ELEMENT_MODAL(
      state: PageSetupState,
      {
        element,
        content,
      }: { element?: PageElement; content: PageContentWithElements },
    ) {
      state.mode = PAGE_MODE.EDIT
      state.currentElement = {
        parentContent: content,
        element,
        wantedIndex: undefined,
      }
      state.showEditElementModal = true
    },
    SHOW_ADD_ELEMENT_MODAL(
      state: PageSetupState,
      { content, index }: { content: PageContentWithElements; index: number },
    ) {
      state.mode = PAGE_MODE.ADD
      state.currentElement = {
        parentContent: content,
        element: undefined,
        wantedIndex: index,
      }
      state.showEditElementModal = true
    },
    SHOW_DELETE_ELEMENT_MODAL(
      state: PageSetupState,
      {
        element,
        content,
      }: { element?: PageElement; content: PageContentWithElements },
    ) {
      state.mode = PAGE_MODE.DELETE
      state.currentElement = {
        parentContent: content,
        element,
        wantedIndex: undefined,
      }
      state.showDeleteElementModal = true
    },
    SHOW_VISIBILITY_ELEMENT_MODAL(
      state: PageSetupState,
      {
        element,
        content,
      }: { element?: PageElement; content: PageContentWithElements },
    ) {
      state.mode = PAGE_MODE.TOGGLING_VISIBILITY
      state.currentElement = {
        parentContent: content,
        element,
        wantedIndex: undefined,
      }
      state.showVisibilityElementModal = true
    },
    SHOW_DELETE_CONTENT_MODAL(state: PageSetupState, content: PageContent) {
      state.mode = PAGE_MODE.DELETE
      state.selectedContent = content
      state.showDeleteContentModal = true
    },
    SHOW_ADD_CONTENT_MODAL(
      state: PageSetupState,
      content: Omit<PageContent, 'page'>,
    ) {
      state.mode = PAGE_MODE.ADD
      state.selectedContent = content
      state.showEditContentModal = true
    },
    SHOW_EDIT_CONTENT_MODAL(state: PageSetupState, content: PageContent) {
      state.mode = PAGE_MODE.EDIT
      state.selectedContent = content
      state.showEditContentModal = true
    },
    SHOW_VISIBILITY_CONTENT_MODAL(state: PageSetupState, content: PageContent) {
      state.mode = PAGE_MODE.TOGGLING_VISIBILITY
      state.selectedContent = content
      state.showVisibilityContentModal = true
    },
    GO_TO_VIEW(state: PageSetupState) {
      state.mode = PAGE_MODE.VIEW
      state.loading--
      state.currentElement = undefined
      state.showEditElementModal = false
      state.showDeleteElementModal = false
      state.showEditContentModal = false
      state.showDeleteContentModal = false
      state.showVisibilityElementModal = false
      state.showVisibilityContentModal = false
    },
    ADD_IMAGE_TO_CONTENT(state) {
      if (state.pageContents.find((page) => page.type === 'HOME_IMAGE')) {
        return
      }
      const content: PageMainImageContent = {
        image: undefined,
        type: 'HOME_IMAGE',
        videoFilename: undefined,
        page: state.currentPage,
        id: undefined,
        isVisible: true,
      }
      state.pageContents = [...state.pageContents, content]
    },
    ADD_SUB_IMAGE_TO_CONTENT(state) {
      if (state.pageContents.find((page) => page.type === 'HOME_SUB_IMAGE')) {
        return
      }
      const content: PageSubMainImageContent = {
        image: undefined,
        type: 'HOME_SUB_IMAGE',
        id: undefined,
        page: state.currentPage,
        isVisible: true,
      }
      state.pageContents = [...state.pageContents, content]
    },
    REMOVE_IMAGES_FROM_CONTENT(state) {
      state.pageContents = state.pageContents.filter(
        (val) => val.type !== 'HOME_IMAGE' && val.type !== 'HOME_SUB_IMAGE',
      )
    },
    SET_MAIN_IMAGE_TEMP(state, payload: string | null) {
      state.mainImageTemp = payload
    },
    SET_SUB_IMAGE_TEMP(state, payload: string | null) {
      state.subImageTemp = payload
    },
    SET_RATIO_TEMP(state, payload: number | null) {
      state.ratioTemp = payload
    },
    SET_SUB_IMAGE_RATIO_TEMP(state, payload: number | null) {
      state.subImageRatioTemp = payload
    },
    SET_PAGE_CHOICES(state: PageSetupState, pageChoices: PageChoice[]) {
      state.pageChoices = pageChoices
    },
  },
  actions: {
    async loadContent({ commit, state, rootState }, page: PageContentLocation) {
      commit('LOAD')
      try {
        state.currentPage = page
        const galleryRecord = rootState.gallery.gallery
        const [pageChoices, pageContents, eventChoices] = await Promise.all([
          PageContentService.getChoices(),
          PageContentService.getAll(state.currentPage, galleryRecord),
          PageContentService.getAllEvents(),
        ])
        state.pageChoices = pageChoices
        state.eventChoices = eventChoices
        const guestsCarousel = rootState.guests.guests
        state.pageContents = pageContents.map((content) => {
          if (content.type === 'HOME_COVER_FLOW_GUEST') {
            if (content.mode !== 'program') {
              return {
                ...content,
                guestIds: sortCarouselGuestIds(
                  content.guestIds ?? [],
                  guestsCarousel,
                ),
              }
            }
            return {
              ...content,
              eventIds: sortCarouselEventIds(
                content.eventIds ?? [],
                rootState.progEvent.progEventList,
                rootState.scenes.scenes,
                guestsCarousel,
                content.programSort ?? 'by_hour',
              ),
            }
          }
          return content
        })
        state.ratioTemp =
          pageContents.find((content) => content.type === 'HOME_IMAGE')
            ?.ratio ?? null
        state.subImageRatioTemp =
          pageContents.find((content) => content.type === 'HOME_SUB_IMAGE')
            ?.ratio ?? null
      } catch (e) {
        ErrorService.handleError(e)
      } finally {
        commit('UNLOAD')
      }
    },

    async saveAllContent(
      { commit, dispatch, state, rootState },
      contents: Omit<PageContent, 'page'>[],
    ) {
      commit('LOAD')
      try {
        state.pageContents = contents
        const contentList = await PageContentService.saveAll(
          contents,
          state.currentPage,
          rootState.gallery.gallery,
        )
        const guestsCarousel = rootState.guests.guests
        state.pageContents = contentList.map((content) => {
          if (content.type === 'HOME_COVER_FLOW_GUEST') {
            if (content.mode !== 'program') {
              return {
                ...content,
                guestIds: sortCarouselGuestIds(
                  content.guestIds ?? [],
                  guestsCarousel,
                ),
              }
            }
            return {
              ...content,
              eventIds: sortCarouselEventIds(
                content.eventIds ?? [],
                rootState.progEvent.progEventList,
                rootState.scenes.scenes,
                guestsCarousel,
                content.programSort ?? 'by_hour',
              ),
            }
          }
          return content
        })
      } catch (e) {
        if (isModifiedElsewhere(e)) {
          SnackbarService.error(
            i18n.tc('PAGESETUP.MODIFIED_ELSEWHERE'),
            MODIFIED_ELSEWHERE_MESSAGE_MS,
          )
          await dispatch('loadContent', state.currentPage)
        } else {
          ErrorService.handleError(e)
        }
      } finally {
        commit('UNLOAD')
      }
    },

    async addContent(
      { commit, dispatch, state, rootState },
      content: Omit<PageContent, 'page'>,
    ) {
      commit('LOAD')
      try {
        const contentList = await PageContentService.saveAll(
          [...state.pageContents, content],
          state.currentPage,
          rootState.gallery.gallery,
        )
        // Used to retrieve the id and page of the newly created HomeContent
        state.pageContents = contentList
      } catch (e) {
        if (isModifiedElsewhere(e)) {
          SnackbarService.error(
            i18n.tc('PAGESETUP.MODIFIED_ELSEWHERE'),
            MODIFIED_ELSEWHERE_MESSAGE_MS,
          )
          await dispatch('loadContent', state.currentPage)
        } else {
          ErrorService.handleError(e)
        }
      } finally {
        commit('UNLOAD')
      }
    },

    async editContent({ commit, state }, content: Omit<PageContent, 'page'>) {
      commit('LOAD')
      try {
        // Find by reference via selectedContent instead of .id lookup.
        // The backend serializes id as "_id" (@JsonProperty("_id")), so
        // .id is undefined on server-returned objects and find() would
        // always match the first item, corrupting the list.
        const foundContent = state.pageContents.find(
          (homeContent) => homeContent === state.selectedContent,
        )
        if (!foundContent) {
          SnackbarService.error('HomeContent not found inside the list')
          return
        }
        const index = state.pageContents.indexOf(foundContent)
        const updated = { ...foundContent, ...content, page: state.currentPage }
        await PageContentService.saveOne(updated as PageContent)
        if (index !== -1) {
          state.pageContents.splice(index, 1, updated as PageContent)
        }
        state.selectedContent = updated as PageContent
      } catch (e) {
        ErrorService.handleError(e)
      } finally {
        commit('UNLOAD')
      }
    },

    async deleteContent({ commit, state }, content: Omit<PageContent, 'page'>) {
      commit('LOAD')
      try {
        // only this block: saving the whole list would also remove the blocks added elsewhere since
        const id = serverId(content)
        if (id != null && id !== '') {
          await PageContentService.deleteOne(id)
        }
        const index = state.pageContents.indexOf(content)
        if (index > -1) {
          state.pageContents.splice(index, 1)
        }
      } catch (e) {
        ErrorService.handleError(e)
      } finally {
        commit('UNLOAD')
      }
    },

    async editElement(
      { commit, state },
      {
        content,
        element,
      }: { content: PageContentWithElements; element: PageElement },
    ) {
      commit('LOAD')
      try {
        // Find by reference via currentElement instead of .id lookup
        // (same _id issue as editContent above).
        const originalElement = state.currentElement?.element
        const foundElement = originalElement
          ? content.elements.find(
              (listElement) => listElement === originalElement,
            )
          : undefined
        if (!foundElement) {
          console.error('Page element not found')
        }
        Object.assign(foundElement ?? {}, element)
        await PageContentService.saveOne(content)
      } catch (e) {
        ErrorService.handleError(e)
      } finally {
        commit('UNLOAD')
      }
    },

    async deleteElement(
      { commit },
      {
        element,
        content,
      }: { element: PageElement; content: PageContentWithElements },
    ) {
      commit('LOAD')
      try {
        const index = content.elements.indexOf(element)
        if (index > -1) {
          content.elements.splice(index, 1)
        }
        // PAGE_LIST is an ordered list - keep positions contiguous
        // CUSTOM_* slots are fixed - keep original positions
        if (content.type === 'PAGE_LIST') {
          content.elements.forEach((el, idx) => {
            el.position = idx
          })
        }
        await PageContentService.saveOne(content)
      } catch (e) {
        ErrorService.handleError(e)
      } finally {
        commit('UNLOAD')
      }
    },

    async addElement(
      { commit, dispatch, state },
      {
        content,
        element,
        index,
      }: {
        content: PageContentWithElements
        element: PageElement
        index: number
      },
    ) {
      commit('LOAD')
      try {
        let maxLengthElements: number | undefined = undefined
        switch (content.type) {
          case 'HOME_CUSTOM_THIRD':
            maxLengthElements = 3
            break
          case 'HOME_CUSTOM_HALF':
            maxLengthElements = 2
            break
          case 'HOME_CUSTOM_FULL':
            maxLengthElements = 1
            break
        }
        if (
          maxLengthElements &&
          (content.elements.length > maxLengthElements ||
            index >= maxLengthElements)
        ) {
          // Only a debug error, should not happen if the code is valid
          SnackbarService.error(
            'Cannot add another element inside the menu, the array is already at max size or the set index is out of bounds',
          )
        } else {
          // PAGE_LIST is an ordered list- new position as max position+1 to avoid collisions
          // CUSTOM_* slots are fixed - keep original positions
          element.position =
            content.type === 'PAGE_LIST'
              ? Math.max(...content.elements.map((e) => e.position)) + 1
              : index
          if (!content.elements[index]) {
            content.elements.splice(index, 1, element)
          } else {
            content.elements.splice(index, 0, element)
          }
        }
        await PageContentService.saveOne(content)
        await dispatch('loadContent', state.currentPage)
      } catch (e) {
        ErrorService.handleError(e)
      } finally {
        commit('UNLOAD')
      }
    },

    async moveElement(
      { commit },
      {
        content,
        oldPosition,
        newPosition,
        element,
      }: {
        content: PageContentWithElements
        oldPosition: number
        newPosition: number
        element: PageElement
      },
    ) {
      commit('LOAD')
      try {
        if (oldPosition === newPosition) {
          return
        } else if (newPosition > oldPosition) {
          // When moving to the right
          content.elements.forEach((el) => {
            if (
              el &&
              el.position <= newPosition &&
              el.position >= oldPosition
            ) {
              el.position--
            }
          })
        } else {
          // When moving to the left
          content.elements.forEach((el) => {
            if (
              el &&
              el.position >= newPosition &&
              el.position <= oldPosition
            ) {
              el.position++
            }
          })
        }
        if (element) {
          element.position = newPosition
        }
        content.elements.sort(
          (e1, e2) => (e1 ? e1.position : 1000) - (e2 ? e2.position : 1000),
        )
        await PageContentService.saveOne(content)
      } catch (e) {
        ErrorService.handleError(e)
      } finally {
        commit('UNLOAD')
      }
    },
  },
  getters: {
    pageContents: (state: PageSetupState) => {
      return state.pageContents
    },
    currentElement: (state: PageSetupState) => {
      return state.currentElement
    },
    pageChoices: (state: PageSetupState): PageChoice[] => {
      return state.pageChoices.sort((sort1: PageChoice, sort2: PageChoice) => {
        const title1 = i18n.tc(`MENU_ENTRIES.TYPE.${sort1.type}`)
        const title2 = i18n.tc(`MENU_ENTRIES.TYPE.${sort2.type}`)
        return title1.localeCompare(title2)
      })
    },
    selectedContent: (state: PageSetupState) => {
      return state.selectedContent
    },
    showEditElementModal: (state: PageSetupState) => {
      return state.showEditElementModal
    },
    showVisibilityElementModal: (state: PageSetupState) => {
      return state.showVisibilityElementModal
    },
    showDeleteElementModal: (state: PageSetupState) => {
      return state.showDeleteElementModal
    },
    showEditContentModal: (state: PageSetupState) => {
      return state.showEditContentModal
    },
    showDeleteContentModal: (state: PageSetupState) => {
      return state.showDeleteContentModal
    },
    showVisibilityContentModal: (state: PageSetupState) => {
      return state.showVisibilityContentModal
    },
  },
})
